import React, { FunctionComponent, useState } from 'react';
import { Input, Button, PageHeader, Row, Col, Divider, Form } from 'antd';
import { toast } from 'react-toastify';
import ObjectiveService from '@services/objectiveService';
import { useRouter } from '@tanstack/react-router';
import { queryClient, teachurKeys } from '@teachur/http-client';
import { useRouterHooks } from '@utils/routerHooks';
import { normalizeMedia } from '@components/forms/helpers';
import AssessmentForm from '@components/forms/AssessmentsForm';
import CourseService from '@services/courseService';
import AIIcon from '@assets/AI_with_notes.webp';
import ObjectiveMediaForm from './ObjectiveMediaForm';
import { useGenerateObjectiveAIMedia } from '@hooks/objectives/useGenerateObjectiveAIMedia';

interface ObjectiveFormProps {
  objective: Partial<Objective>;
}

const showAIErrorToast = () => {
  toast.error('Please enter Objective name and select AI model.', {
    style: { textAlign: 'center' },
  });
};

const buttonAIStyles = {
  border: 'none',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  left: '180px',
  bottom: '-25px',
  zIndex: 9,
} as React.CSSProperties;

const ObjectiveForm: FunctionComponent<ObjectiveFormProps> = ({ objective }) => {
  const router = useRouter();
  const { match } = useRouterHooks();
  const courseId = match.params.courseId ? parseInt(match.params.courseId, 10) : undefined;

  const [updateLoading, setUpdateLoading] = useState(false);
  const [assessments, setAssessments] = useState<Assessment[]>(objective.assessments || []);
  const [objectiveName, setObjectiveName] = useState(objective.name);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [form] = Form.useForm();
  const { setFieldsValue, validateFields } = form;

  const [assessmentsAILessons, setAssessmentsAILessons] = useState<any>([
    {
      question: '',
      answers: [
        {
          text: '',
          correct: false,
        },
      ],
    },
  ]);

  const { mutate, isLoading } = useGenerateObjectiveAIMedia();
  const [objectiveAIMedia, setObjectiveAIMedia] = useState<ObjectiveAIMedia[]>([]);
  const [selectedAIModel, setSelectedAIModel] = useState('');

  const handleSubmit = () => {
    validateFields()
      .then((values) => {
        if (objective.id) {
          setUpdateLoading(true);

          const normalizedMedia = values.media ? normalizeMedia(values.media) : [];

          const normalizedAssessments = values.assessments.map((a: any) => ({
            ...a,
            answers: a.answers.map((ans: any) => ({
              ...ans,
              correct: ans.correct || false,
            })),
          }));

          values.media = normalizedMedia;
          values.assessments = normalizedAssessments;

          const savePromise =
            objective.id === -1
              ? ObjectiveService.create(values)
              : ObjectiveService.update(objective.id, {
                  ...values,
                  id: objective.id,
                });

          savePromise.then((d) => {
            const successMessage = objective.id === -1 ? 'Create' : 'Update';

            toast.success(`${successMessage} successful!`);

            if (courseId) {
              queryClient.invalidateQueries({ queryKey: teachurKeys.courses.detail(courseId) });
            }

            if (objective.id) {
              queryClient.invalidateQueries({
                queryKey: teachurKeys.objectives.detail(objective.id),
              });
            }

            if (courseId) {
              router.navigate({
                to: `/courses/${courseId}/objectives/${d.id}`,
              });
            } else {
              router.navigate({
                to: `/objectives/${d.id}`,
              });
            }
          });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error('Please enter all required fields');
      });
  };

  const AILessonTopicaAsessments = async () => {
    try {
      if (objectiveName == '' || objectiveName == undefined) {
        return;
      }
      const sendData = {
        courseName: '',
        lessonName: '',
        topicName: objectiveName,
        userIdentifier: '',
      };
      setAssessmentsLoading(true);
      const LessonCourseTopicDes = await CourseService.getCourseLessonTopicAsessmentsAI(sendData);
      if (LessonCourseTopicDes.success) {
        setAssessmentsLoading(false);
        const storeaDta = [];
        for (let i = 0; i < LessonCourseTopicDes.data.completion.mcq_questions.length; i++) {
          const storeaDtaAnswrs = LessonCourseTopicDes.data.completion.mcq_questions[i];

          // if (storeaDtaAnswrs.length > 0) {
          storeaDta.push({
            question: storeaDtaAnswrs.question,
            answers: [
              {
                id: -1,
                text: storeaDtaAnswrs.options[0].option.substr(
                  storeaDtaAnswrs.options[0].option.indexOf(' ') + 1
                ),
                correct: storeaDtaAnswrs.options[0].is_correct,
              },
              {
                id: -1,
                text: storeaDtaAnswrs.options[1].option.substr(
                  storeaDtaAnswrs.options[0].option.indexOf(' ') + 1
                ),
                correct: storeaDtaAnswrs.options[1].is_correct,
              },
              {
                id: -1,
                text: storeaDtaAnswrs.options[2].option.substr(
                  storeaDtaAnswrs.options[0].option.indexOf(' ') + 1
                ),
                correct: storeaDtaAnswrs.options[2].is_correct,
              },
              {
                id: -1,
                text: storeaDtaAnswrs.options[3].option.substr(
                  storeaDtaAnswrs.options[0].option.indexOf(' ') + 1
                ),
                correct: storeaDtaAnswrs.options[3].is_correct,
              },
            ],
          });
          // }
        }
        setAssessmentsAILessons(storeaDta);
      } else {
        setAssessmentsLoading(false);
      }
    } catch (err) {
      setAssessmentsLoading(false);
    }
  };

  const handleObjectiveAIMedia = async () => {
    if (!objectiveName || !selectedAIModel) {
      showAIErrorToast();
      return;
    }
    mutate(
      { objective: objectiveName, aiModel: selectedAIModel },
      {
        onSuccess: ({ data }) => {
          setObjectiveAIMedia(data.objective);
        },
        onError: (error: any) => toast.error(error?.statusText),
      }
    );
  };

  return (
    <Form onFinish={handleSubmit} form={form}>
      <Row gutter={24}>
        <Col span={24}>
          <PageHeader
            style={{
              background: '#fff',
              padding: '16px 16px 0',
            }}
            extra={[
              <Button key="save" type="primary" htmlType="submit" loading={updateLoading}>
                Save
              </Button>,
            ]}
            title={
              <>
                <h2 style={{ margin: '8px 0' }}>
                  {objective.id === -1 ? 'Create ' : 'Edit '}Objective
                </h2>
              </>
            }
          />
          <div
            style={{
              background: '#fff',
              padding: '16px 16px',
            }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={objective.name}
            >
              <Input onChange={(e) => setObjectiveName(e.target.value)} />
            </Form.Item>

            <ObjectiveMediaForm
              media={objective.media || []}
              onObjectiveAIMedia={handleObjectiveAIMedia}
              objectiveItems={objectiveAIMedia}
              setItems={setObjectiveAIMedia}
              isLoading={isLoading}
              onSelectModel={setSelectedAIModel}
            />

            <Divider />
            {objectiveName && objectiveName != '' && (
              <Button
                type="default"
                disabled={assessmentsLoading}
                loading={assessmentsLoading}
                style={buttonAIStyles}
                onClick={AILessonTopicaAsessments}
              >
                <img
                  style={{
                    height: '33px',
                    width: '33px',
                  }}
                  src={AIIcon}
                />
              </Button>
              // <Button
              //   loading={assessmentsLoading}
              //   className="ai-btn-objective-assessment"
              //   type="primary"
              // >
              //   {' '}
              //   {!assessmentsLoading && (
              //     <img
              //       style={{
              //         height: '35px',
              //         width: '35px',
              //       }}
              //       alt="AIIcon"
              //       onClick={() => {
              //         AILessonTopicaAsessments();
              //       }}
              //       src={AIIcon}
              //     />
              //   )}
              // </Button>
            )}
            <AssessmentForm
              assessmentsAILessons={assessmentsAILessons}
              assessments={assessments}
              setAssessments={setAssessments}
              setFieldsValue={setFieldsValue}
            />

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                Save
              </Button>
            </Form.Item>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default ObjectiveForm;
